/* eslint-disable complexity */
import { trackCategoriesLayoutElementCtaClick } from "@faire/web-api/events/categories/click/layoutElementCta";
import { trackCategoriesLoggedOutCategoryBottomCarouselClick } from "@faire/web-api/events/categories/click/loggedOutCategoryBottomCarousel";
import { trackCategoriesLoggedOutCategoryTopCarouselClick } from "@faire/web-api/events/categories/click/loggedOutCategoryTopCarousel";
import { trackCategoriesLayoutElementView } from "@faire/web-api/events/categories/view/layoutElement";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { Column, Flex, HorizontalLine } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { Typography, TypographyStyles } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import React, { useCallback } from "react";
import styled from "styled-components";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { CommunityCategoryBannerBase as CommunityCategoryBanner } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Banner/ByType/CommunityCategoryBanner";
import { LayoutElementBasedBanner } from "@faire/retailer/components/LayoutElementRenderer/LayoutElementBasedBanner";
import { LayoutElementBasedCarousel } from "@faire/retailer/components/LayoutElementRenderer/LayoutElementBasedCarousel";
import { TitleStyles } from "@faire/retailer/components/LayoutElementRenderer/styles/TitleStyles";
import { ShowroomTitleBanner } from "@faire/retailer/features/Showroom/ApparelCategory/ShowroomTitleBanner";
import { useSettingLoggedOutCategoryPageRedesign } from "@faire/retailer/settings/useSettingLoggedOutCategoryPageRedesign";
import { LoggedOutCategoryPageBreadcrumb } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageAboveFold/LoggedOutCategoryPageBreadcrumb";
import { LoggedOutCategoryPageHeroBanner } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/components/LoggedOutCategoryPageLayoutElements/__internal__/LoggedOutCategoryPageHeroBanner";
import { useLoggedOutCategoryPageCurrentCategory } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentCategory";

interface IProps {
  layoutElement: ILayoutElement;
  placement: keyof typeof ILayoutElement.Placement | undefined;
  requestId?: string;
  shouldDisplayShowroomBanner?: boolean;
}

export const LoggedOutCategoryPageLayoutElementRenderer: React.FC<IProps> = ({
  layoutElement,
  requestId,
  shouldDisplayShowroomBanner,
  placement,
}) => {
  const isLoggedOutCategoryPageRedesign =
    useSettingLoggedOutCategoryPageRedesign();
  const category = useLoggedOutCategoryPageCurrentCategory();
  const { isMobile, isTabletAndAbove, isDesktop } = useViewport();
  const avatarGridOnItemClick = useCallback((categoryLink: string) => {
    trackCategoriesLoggedOutCategoryTopCarouselClick(categoryLink);
  }, []);

  const ref = useDoActionWhenVisible(() =>
    trackCategoriesLayoutElementView(
      layoutElement.on_click_event_name ?? "",
      requestId ?? "",
      ""
    )
  );
  if (layoutElement.placement !== placement) {
    return null;
  }
  switch (layoutElement.type) {
    case ILayoutElement.Type.AVATAR_CAROUSEL_WITH_GRID: {
      const tilesLength = layoutElement.layout_element_data?.length ?? 0;
      const itemsPerSlide = {
        mobileAndAbove: Math.min(3, tilesLength),
        tabletAndAbove: Math.min(3, tilesLength),
        desktopAndAbove: Math.min(3, tilesLength),
        xLargeAndAbove: Math.min(5, tilesLength),
      };

      return (
        <Wrapper>
          <HeaderWrapper>
            <TitleH1>{layoutElement.main_text}</TitleH1>
            <TitleH2>{layoutElement.description_text}</TitleH2>
          </HeaderWrapper>
          <StyledCarouselWrapper
            data-test-id="embla-carousel-wrapper"
            $itemPerSlide={itemsPerSlide}
            $type={layoutElement.type}
          >
            <LayoutElementBasedCarousel
              layoutElement={layoutElement}
              requestId={requestId ?? ""}
              itemsPerSlide={itemsPerSlide}
              onItemClick={avatarGridOnItemClick}
              hideTitle
            />
            {isMobile && layoutElement.layout_element_data.length > 0 ? (
              <>
                <Spacer size="3x" />
                <StyledHorizontalLine />
                <Spacer size="4x" />
              </>
            ) : null}
          </StyledCarouselWrapper>
          {isDesktop ? <Spacer size="3x" /> : null}
        </Wrapper>
      );
    }
    case ILayoutElement.Type.AVATAR_CAROUSEL: {
      const tilesLength = layoutElement.layout_element_data?.length ?? 0;
      const itemsPerSlide = {
        mobileAndAbove: Math.min(3, tilesLength),
        tabletAndAbove: Math.min(6, tilesLength),
        desktopAndAbove: Math.min(8, tilesLength),
      };
      return (
        <StyledCarouselWrapper
          data-test-id="embla-carousel-wrapper"
          $itemPerSlide={itemsPerSlide}
          $type={layoutElement.type}
          style={{ alignSelf: isTabletAndAbove ? "center" : "unset" }}
        >
          <LayoutElementBasedCarousel
            layoutElement={layoutElement}
            requestId={requestId ?? ""}
            itemsPerSlide={itemsPerSlide}
            onItemClick={trackCategoriesLoggedOutCategoryBottomCarouselClick}
          />
          <Spacer height="6x" />
        </StyledCarouselWrapper>
      );
    }
    case ILayoutElement.Type.MID_PRICE_POINT_PLUS_PAGE_TITLE:
      if (shouldDisplayShowroomBanner) {
        return (
          <>
            <ShowroomTitleBanner
              layoutElement={layoutElement}
              category={category}
              isLoggedOutPage
            />
            {isLoggedOutCategoryPageRedesign ? (
              <Spacer height={{ mobileAndAbove: "3x", tabletAndAbove: "4x" }} />
            ) : null}
          </>
        );
      }
      return null;
    case ILayoutElement.Type.COMMUNITY_CATEGORY_BANNER:
      return (
        <div ref={ref}>
          <CommunityCategoryBanner
            layoutElement={layoutElement}
            onCTAClick={(eventName: string, requestId: string) => {
              trackCategoriesLayoutElementCtaClick(eventName, requestId);
            }}
            requestId={requestId}
          />
        </div>
      );
    case ILayoutElement.Type.SHOWROOM_VALUES_TITLE_BANNER:
    case ILayoutElement.Type.FULL_WIDTH_IMAGE_BANNER:
      return (
        <>
          <LayoutElementBasedBanner
            layoutElement={layoutElement}
            requestId={requestId ?? ""}
            onVisible={() =>
              trackCategoriesLayoutElementView(
                layoutElement.on_click_event_name ?? "",
                requestId ?? "",
                ""
              )
            }
          />
          {isLoggedOutCategoryPageRedesign ? (
            <Spacer height={{ mobileAndAbove: "3x", tabletAndAbove: "4x" }} />
          ) : null}
        </>
      );
    case ILayoutElement.Type.MARKETING_BANNER:
      return <LoggedOutCategoryPageHeroBanner layoutElement={layoutElement} />;
    case ILayoutElement.Type.TALL_CATEGORY_TILE:
      return (
        <StyledSubcategoryCarousel>
          <LayoutElementBasedCarousel
            arrowOffsetX={0}
            layoutElement={layoutElement}
            requestId={requestId ?? ""}
            onItemClick={trackCategoriesLoggedOutCategoryBottomCarouselClick}
            showNavigationArrows={{
              mobileAndAbove: false,
              desktopAndAbove: true,
            }}
          />
        </StyledSubcategoryCarousel>
      );

    case ILayoutElement.Type.SIMPLE_TEXT_BANNER:
      // "all products" subheader above filter row
      if (!layoutElement.description_text) {
        return (
          <Typography
            variant={{
              mobileAndAbove: "pageHeaderSerifRegular",
              xLargeAndAbove: "displaySSerifRegular",
            }}
          >
            {layoutElement.main_text}
          </Typography>
        );
      }
      // header and subheader
      return (
        <>
          <Typography
            as="h1"
            variant={{
              mobileAndAbove: "displaySSerifRegular",
              xLargeAndAbove: "displayMSerifRegular",
            }}
          >
            {layoutElement.main_text}
          </Typography>
          <Spacer height="1x" />
          <Typography
            variant={{
              mobileAndAbove: "paragraphSansRegular",
              xLargeAndAbove: "subheadingSansRegular",
            }}
          >
            {layoutElement.description_text}
          </Typography>
          <Spacer height={{ mobileAndAbove: "3x", tabletAndAbove: "4x" }} />
        </>
      );
    case ILayoutElement.Type.BREADCRUMB:
      return <StyledBreadcrumbWithLOCPRedesign />;
    default:
      return null;
  }
};

const StyledCarouselWrapper = styled.div<{
  $itemPerSlide: ResponsiveValues<number>;
  $type: "AVATAR_CAROUSEL" | "AVATAR_CAROUSEL_WITH_GRID";
}>`
  min-width: 0;

  --item-per-slide-xlarge: ${(props) =>
    props.$itemPerSlide.xLargeAndAbove ?? props.$itemPerSlide.desktopAndAbove};
  --item-per-slide-desktop: ${(props) => props.$itemPerSlide.desktopAndAbove};
  --item-per-slide-tablet: ${(props) => props.$itemPerSlide.tabletAndAbove};
  --item-per-slide-mobile: ${(props) => props.$itemPerSlide.mobileAndAbove};
  ${TitleStyles.Container} {
    align-self: center;
    align-items: center;
    width: max-content;
  }

  ${responsiveMediaQueries.mobile} {
    ${TitleStyles.Container} {
      padding-left: 20px;
      align-self: flex-start;
    }
  }

  ${responsiveMediaQueries.tablet} {
    // next button width is 40px;
    margin-right: 40px;

    width: calc(
      88px * var(--item-per-slide-tablet) + 16px *
        (var(--item-per-slide-tablet) - 1)
    );
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    width: calc(
      88px * var(--item-per-slide-desktop) + 16px *
        (var(--item-per-slide-desktop) - 1)
    );
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    width: calc(
      88px * var(--item-per-slide-xlarge) + 16px *
        (var(--item-per-slide-xlarge) - 1)
    );
  }
`;

const StyledHorizontalLine = styled(HorizontalLine)`
  width: 100vw;
  margin-left: calc(-50vw + 50%);
`;

const Wrapper = styled(Flex).attrs({
  direction: {
    tabletAndAbove: "row",
    mobileAndAbove: "column-reverse",
  },
})`
  width: 100%;
  ${responsiveMediaQueries.mobile} {
    padding: 0 16px;
  }
`;

// eslint-disable-next-line @faire/prefer-slate-attrs
const HeaderWrapper = styled(Column)`
  ${responsiveMediaQueries.desktopAndAbove} {
    flex: 1 0 50%;
    padding-right: 80px;
  }

  ${responsiveMediaQueries.tablet} {
    flex: 1 0 53%;
    padding-right: 40px;
  }

  ${responsiveMediaQueries.mobile} {
    align-items: center;
    justify-content: center;
    text-align: center;
  }
`;

// For SEO purpose, we have to make this h1 tag, please don't change unless
// consulted with the SEO team
const TitleH1 = styled.h1`
  ${TypographyStyles({ variant: "pageHeaderSerifRegular" })}
  margin-bottom: 8px;

  ${responsiveMediaQueries.mobile} {
    text-align: center;
  }
`;

// For SEO purpose, we have to make this h2 tag, please don't change unless
// consulted with the SEO team
const TitleH2 = styled.h2`
  ${TypographyStyles({})}

  max-width: 100%;

  ${responsiveMediaQueries.mobile} {
    max-width: 400px;
  }
`;

const StyledSubcategoryCarousel = styled.div`
  padding-bottom: ${getSpacing("4x")};
  ${responsiveMediaQueries.mobile} {
    ${TitleStyles.Container} {
      padding-left: ${getSpacing("2x")};
    }
    margin: ${getSpacing(0, "-2x")};
    padding-bottom: ${getSpacing("3x")};
  }
`;

const StyledBreadcrumbWithLOCPRedesign = styled(
  LoggedOutCategoryPageBreadcrumb
)`
  max-width: unset;
  padding: ${getSpacing("4x", 0, "1x", 0)};
  ${responsiveMediaQueries.tabletAndBelow} {
    padding: ${getSpacing("3x", 0, "1x", 0)};
  }
`;
